import { Link } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { PORTAL_NAVIGATION_ITEMS } from "@/lib/portal-navigation";
import { WORKSPACE_NAVIGATION_GROUPS, portalNavigationItem } from "@/lib/portal-navigation";
import { usePortalExperience } from "@/lib/portal-experience";
import { PortalExperienceSwitcher } from "./PortalExperienceSwitcher";

export function PortalMobileNavigation({ liveMode }: Readonly<{ liveMode: boolean }>) {
  const [open, setOpen] = useState(false);
  const { experience } = usePortalExperience();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-card text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden"
          aria-label="Open primary navigation"
        >
          <Menu className="h-4 w-4" aria-hidden="true" />
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-[min(22rem,calc(100vw-2rem))] overflow-y-auto border-border bg-background p-0 text-foreground [&>button]:right-5 [&>button]:top-5"
      >
        <SheetHeader className="border-b border-border px-5 pb-4 pt-5 text-left">
          <SheetTitle>
            {experience === "workspace" ? "Workspace Preview" : "Portal navigation"}
          </SheetTitle>
          <SheetDescription>
            {experience === "workspace"
              ? "Follow the warranty issue from signal through verified result."
              : "Browse governed warranty intelligence workspaces."}
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-5 p-5">
          <div className="rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-xs text-primary">
            {liveMode ? "Governed API snapshot" : "Fixture snapshot"}
          </div>
          <PortalExperienceSwitcher compact />
          {experience === "classic" ? (
            <ClassicMobileNavigation onNavigate={() => setOpen(false)} />
          ) : (
            <WorkspaceMobileNavigation onNavigate={() => setOpen(false)} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ClassicMobileNavigation({ onNavigate }: Readonly<{ onNavigate(): void }>) {
  return (
    <nav aria-label="Subscriber Portal navigation" className="grid gap-1">
      {PORTAL_NAVIGATION_ITEMS.map((item) => (
        <MobileNavigationLink key={item.to} item={item} onNavigate={onNavigate} />
      ))}
    </nav>
  );
}

function WorkspaceMobileNavigation({ onNavigate }: Readonly<{ onNavigate(): void }>) {
  return (
    <nav aria-label="Workspace Preview navigation" className="space-y-5">
      {WORKSPACE_NAVIGATION_GROUPS.map((group) => (
        <section key={group.id} aria-labelledby={`mobile-${group.id}-label`}>
          <div className="mb-1 px-3">
            <h2 id={`mobile-${group.id}-label`} className="text-sm font-semibold">
              {group.label}
            </h2>
            <p className="text-xs text-muted-foreground">{group.description}</p>
          </div>
          <div className="grid gap-1">
            {group.routes.map((route) => (
              <MobileNavigationLink
                key={route}
                item={portalNavigationItem(route)}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </section>
      ))}
    </nav>
  );
}

function MobileNavigationLink({
  item,
  onNavigate,
}: Readonly<{
  item: (typeof PORTAL_NAVIGATION_ITEMS)[number];
  onNavigate(): void;
}>) {
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      className="rounded-lg px-3 py-3 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="block text-sm font-semibold">{item.label}</span>
      <span className="mt-0.5 block text-xs text-muted-foreground">{item.description}</span>
    </Link>
  );
}
